/**
 * Starter slide templates — one per *layout* the editor knows about.
 *
 * When the user picks a layout from the add-slide modal, we seed the new
 * slide's ``bodyHtml`` with a small, working markup snippet that the
 * renderer can already render. The detector in ``layouts/detectLayout``
 * picks the layout up from the slug, so the slug here is also the hint
 * the inspector uses to dispatch the right form.
 *
 * The bodies are intentionally minimal — they mirror the *structural*
 * skeleton of the canonical pitch-deck slides (see
 * ``canonicalTemplate.ts``) without the production copy. Editors fill in
 * the words; the renderer handles styling via theme CSS.
 */

import type { Slide } from "./types";

export type SlideTemplateKind =
  | "opener"
  | "two-col"
  | "paths"
  | "asks"
  | "pipeline"
  | "ledger"
  | "stop-press"
  | "closing"
  | "meta"
  | "generic";

export interface SlideTemplate {
  kind: SlideTemplateKind;
  label: string;
  /** One-line description shown in the layout picker. */
  description: string;
  /**
   * Slug stamped onto the new slide. The autosave detector reads
   * ``detectLayout`` from this slug, so each template's slug must trigger
   * the matching form.
   */
  slug: string;
  /** Section header — purely cosmetic on the rail; editors can change it. */
  section: string;
  /** Placeholder title shown in the inspector form. */
  titleHtml: string;
  /** Whether the slide should be hidden from the rendered deck initially. */
  meta: boolean;
  /** Starting HTML body — a working but bare-bones layout skeleton. */
  bodyHtml: string;
}

/**
 * Build a fresh ``Slide`` from a template. The caller can override any
 * field; defaults match the renderer's conventions (see
 * ``app/services/decks.py::empty_deck`` for the server-side mirror).
 */
export function newSlideFromTemplate(
  template: SlideTemplate,
  overrides: Partial<Slide> = {},
): Slide {
  return {
    section: template.section,
    slug: template.slug,
    titleHtml: template.titleHtml,
    meta: template.meta,
    bodyHtml: template.bodyHtml,
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
    ...overrides,
  };
}

export const SLIDE_TEMPLATES: SlideTemplate[] = [
  {
    kind: "opener",
    label: "Opener",
    description:
      "Thesis-line title slide with logo lockup. Use for the deck cover.",
    slug: "opener-new",
    section: "I · OPENER",
    titleHtml: "Your thesis goes here.",
    meta: false,
    bodyHtml:
      '    <div class="logo-bar">\n' +
      '        <img src="flexcompute-logo.svg" class="fc-logo" alt="Flexcompute">\n' +
      '        <img src="../_common/assets/thread-logo-lockup-white.svg" class="thread-logo" alt="Thread">\n' +
      "    </div>\n" +
      '    <div style="margin-top: 0.6em;">\n' +
      '        <h2 style="font-size: 0.9em; font-style: normal; color: var(--fc-gray); margin-bottom: 0.6em;">\n' +
      "            Subtitle / kicker line.\n" +
      "        </h2>\n" +
      "    </div>",
  },
  {
    kind: "two-col",
    label: "Two-column",
    description:
      "CSS-grid narrative on the left, synced panel on the right (data-black-market style).",
    slug: "two-col-new",
    section: "II · DBM",
    titleHtml: "Two-column slide",
    meta: false,
    bodyHtml:
      '    <div class="two-col-grid" style="display: grid; grid-template-columns: 1fr 1.2fr; gap: 1.2em;">\n' +
      '        <div class="trail">\n' +
      '            <div class="trail-beat" data-panel="0">First narrative beat.</div>\n' +
      '            <div class="trail-beat" data-panel="1">Second beat.</div>\n' +
      "        </div>\n" +
      '        <div class="panel">\n' +
      "            <!-- Right-side image / chart goes here -->\n" +
      "        </div>\n" +
      "    </div>",
  },
  {
    kind: "paths",
    label: "Paths",
    description:
      "Selection-model slide: pick a cell, walk back through provenance (provenance-dag style).",
    slug: "paths-new",
    section: "III · MODEL",
    titleHtml: "Selection is the action.",
    meta: false,
    bodyHtml:
      '    <h1>Selection is the action.</h1>\n' +
      '    <h2>Pick a cell in the flat view; walk back through provenance.</h2>',
  },
  {
    kind: "asks",
    label: "Asks",
    description:
      "Card-grid payoff slide (what-thread-unlocks / not-solving style).",
    slug: "asks-new",
    section: "IV · PAYOFF",
    titleHtml: "What this unlocks",
    meta: false,
    bodyHtml:
      '    <h1 style="font-size: 1.1em; margin-bottom: 0.4em;">What this unlocks</h1>\n' +
      '    <h2>Three capabilities. One sentence each.</h2>\n' +
      "\n" +
      '    <div class="card-grid" style="gap: 0.3em;">\n' +
      '        <div class="card fragment fade-up" style="border-top: 2px solid var(--fc-orange); padding: 0.45em 0.65em;">\n' +
      '            <h3 style="margin-top: 0; font-size: 0.55em;">First</h3>\n' +
      '            <p style="font-size: 0.52em; margin: 0;">Short description.</p>\n' +
      "        </div>\n" +
      '        <div class="card fragment fade-up" style="border-top: 2px solid var(--fc-orange); padding: 0.45em 0.65em;">\n' +
      '            <h3 style="margin-top: 0; font-size: 0.55em;">Second</h3>\n' +
      '            <p style="font-size: 0.52em; margin: 0;">Short description.</p>\n' +
      "        </div>\n" +
      '        <div class="card fragment fade-up" style="border-top: 2px solid var(--fc-green); padding: 0.45em 0.65em;">\n' +
      '            <h3 style="margin-top: 0; font-size: 0.55em;">Third</h3>\n' +
      '            <p style="font-size: 0.52em; margin: 0;">Short description.</p>\n' +
      "        </div>\n" +
      "    </div>",
  },
  {
    kind: "pipeline",
    label: "Pipeline",
    description:
      "Horizontal arrow-flow showing stages. JSON-fallback form for now.",
    slug: "pipeline-new",
    section: "III · MODEL",
    titleHtml: "Pipeline overview",
    meta: false,
    bodyHtml:
      '    <h1>Pipeline overview</h1>\n' +
      '    <div class="pipeline" style="display: flex; gap: 0.4em; align-items: center;">\n' +
      '        <div class="stage">Stage 1</div>\n' +
      '        <div class="arrow">&rarr;</div>\n' +
      '        <div class="stage">Stage 2</div>\n' +
      '        <div class="arrow">&rarr;</div>\n' +
      '        <div class="stage">Stage 3</div>\n' +
      "    </div>",
  },
  {
    kind: "ledger",
    label: "Ledger",
    description:
      "Two-column receipts / scorecard list. JSON-fallback form for now.",
    slug: "ledger-new",
    section: "IV · PAYOFF",
    titleHtml: "Ledger",
    meta: false,
    bodyHtml:
      '    <h1>Ledger</h1>\n' +
      '    <table class="ledger" style="width: 100%; border-collapse: collapse;">\n' +
      "        <tr><td>Line item</td><td>Value</td></tr>\n" +
      "        <tr><td>Line item</td><td>Value</td></tr>\n" +
      "    </table>",
  },
  {
    kind: "stop-press",
    label: "Stop press",
    description:
      "Single-statement attention-grabber with a callout box. JSON-fallback form for now.",
    slug: "stop-press-new",
    section: "II · DBM",
    titleHtml: "Stop press",
    meta: false,
    bodyHtml:
      '    <div class="stop-press" style="border: 2px solid var(--fc-orange); padding: 0.8em;">\n' +
      '        <h1 style="margin: 0;">Headline goes here.</h1>\n' +
      '        <p style="margin: 0.4em 0 0;">Subhead with context.</p>\n' +
      "    </div>",
  },
  {
    kind: "closing",
    label: "Closing",
    description:
      "Cinematic close with a logo or thesis reprise. JSON-fallback form for now.",
    slug: "closing-new",
    section: "V · CLOSE",
    titleHtml: "",
    meta: false,
    bodyHtml:
      '    <!-- Cinematic close: thesis reprise / T-mark animation. -->\n' +
      '    <div class="close-frame" style="text-align: center;">\n' +
      "        <h1>Closing line.</h1>\n" +
      "    </div>",
  },
  {
    kind: "meta",
    label: "Meta / note",
    description:
      "Speaker-notes slide hidden from the rendered deck. Toggle the meta flag later if you want it visible.",
    slug: "meta-new",
    section: "META",
    titleHtml: "Internal note",
    meta: true,
    bodyHtml: "    <p>Speaker notes / context. Hidden from the rendered deck.</p>",
  },
  {
    kind: "generic",
    label: "Generic / blank",
    description:
      "Empty slide with no special layout — falls through to the JSON editor.",
    slug: "blank-new",
    section: "",
    titleHtml: "Untitled slide",
    meta: false,
    bodyHtml: "    <h1>Untitled slide</h1>\n    <p>Body.</p>",
  },
];
